import { pb } from "@/lib/pocketbase";
import { pbRepository } from "./pocketbase-repository";
import { auth } from "./auth.service";

/**
 * Übe-Daten eines Themas (Collection `practice_data`, siehe SCHEMA.md).
 *
 * Karteikarten, Lernpfad, Vortragsrunden und Tagesstatistik teilen sich EINE
 * Zeile pro Thema statt vier eigener Collections – jedes Feld ein JSON-Array.
 * Jedes Element bringt seine eigene `id` mit (`crypto.randomUUID()`), da es
 * keine PocketBase-Zeilen-ID mehr je Karte/Lektion/Runde gibt. Schreiben ist
 * read-modify-write auf das ganze Feld; die Domänen-Services
 * (flashcards/path/speech-rounds/learn-stats) kapseln das.
 */

export interface PracticeData {
  flashcards: Record<string, unknown>[];
  pathLessons: Record<string, unknown>[];
  speechRounds: Record<string, unknown>[];
  learnStats: Record<string, unknown>[];
}

type Row = PracticeData & { id: string };

const repo = pbRepository<Record<string, unknown>>("practice_data");
const cache = new Map<string, Row>();

function parseArr(v: unknown): Record<string, unknown>[] {
  if (Array.isArray(v)) return v as Record<string, unknown>[];
  if (typeof v === "string" && v.trim()) {
    try {
      const p: unknown = JSON.parse(v);
      return Array.isArray(p) ? (p as Record<string, unknown>[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function toRow(r: Record<string, unknown>): Row {
  return {
    id: String(r.id ?? ""),
    flashcards: parseArr(r.flashcards),
    pathLessons: parseArr(r.pathLessons),
    speechRounds: parseArr(r.speechRounds),
    learnStats: parseArr(r.learnStats),
  };
}

async function ensureRow(topicId: string): Promise<Row> {
  const hit = cache.get(topicId);
  if (hit) return hit;
  const found = await repo.list({ filter: pb.filter("topic = {:t}", { t: topicId }), perPage: 1 });
  const rec =
    found[0] ??
    (await repo.create({
      owner: auth.currentUser()?.id ?? "",
      topic: topicId,
      flashcards: [],
      pathLessons: [],
      speechRounds: [],
      learnStats: [],
    }));
  const row = toRow(rec);
  cache.set(topicId, row);
  return row;
}

export const practiceDataService = {
  /** Alle vier Arrays eines Themas lesen (aus Cache, wenn schon geladen). */
  async read(topicId: string): Promise<PracticeData> {
    return ensureRow(topicId);
  },

  /** Einzelne Felder ersetzen (nur die übergebenen werden serverseitig geschrieben). */
  async write(topicId: string, patch: Partial<PracticeData>): Promise<PracticeData> {
    const row = await ensureRow(topicId);
    Object.assign(row, patch);
    await repo.update(row.id, patch);
    return row;
  },

  /**
   * `learnStats`-Arrays aller Themen des Nutzers holen, ohne die anderen
   * (potenziell großen) Felder mitzuladen – für den geräteübergreifenden
   * Streak-Abgleich (siehe streak.service.ts).
   */
  async listAllLearnStats(): Promise<Record<string, unknown>[][]> {
    const rows = await pb.collection("practice_data").getFullList<Record<string, unknown>>({
      fields: "learnStats",
      requestKey: null,
    });
    return rows.map((r) => parseArr(r.learnStats));
  },
};
